"use client";

import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";

// local component imports
import VideoCard from "../VideoCard";
import { CarouselArrow } from "../../../GenericComponents/DyamicCarousel/Arrow";

// services and utils imports
import { getProductsByIdsData, getProductsFromByIdsResponse } from "../../../../../../api/plpApi";
import useSlickSlideInert from "../../../../../../customHooks/useSlickSlideInert";
import useIntersectionObserver from "../../../../../../customHooks/intersectionObserver";

// slick css imports
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/**
 * Placeholder shown before the widget enters the viewport — preserves layout
 * and displays the first video thumbnail without fetching video media.
 * @param {object} props - Component props
 * @param {object} props.video - First video in the trail
 * @returns {React.ReactElement} Placeholder card
 */
const VideoTrailPlaceholder = ({ video }) => (
  <div className="flex justify-center px-2 md:px-10">
    <div
      className="w-full max-w-[280px] overflow-hidden rounded-2xl bg-grey-100 md:max-w-none"
      style={{ aspectRatio: "2/3" }}
      aria-hidden="true"
    >
      {video?.thumbnail ? (
        <img src={video.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : null}
    </div>
  </div>
);

VideoTrailPlaceholder.propTypes = {
  video: PropTypes.object,
};

/**
 * Infinite slick clones duplicate `<video src>`, causing a second network fetch.
 * Clones are visual-only — strip their media sources so each URL loads once.
 * @param {HTMLElement|null} container - The carousel container element
 * @returns {void}
 */
function stripClonedSlideVideos(container) {
  if (!container) {
    return;
  }
  container.querySelectorAll(".slick-cloned video").forEach((video) => {
    video.pause();
    video.removeAttribute("src");
    video.removeAttribute("data-src");
    video.preload = "none";
  });
}

/**
 * Normalizes slick slide indices when infinite mode wraps past the last slide.
 * @param {number} index - Raw slick slide index
 * @param {number} length - Number of real slides
 * @returns {number} Index mapped to the videos array
 */
function normalizeSlideIndex(index, length) {
  if (!length) {
    return 0;
  }
  return ((index % length) + length) % length;
}

/**
 * VideoTrail renders a slick carousel of video cards.
 * @param {object} props - Component props
 * @param {Array} props.videos - Array of video objects to display
 * @param {object} props.sectionPadding - Horizontal padding styles
 * @returns {React.ReactElement} The rendered video trail carousel
 */
const VideoTrail = ({ videos, isMobile, sectionPadding }) => {
  const safeVideos = Array.isArray(videos) ? videos : [];
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoProducts, setVideoProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isInView = useIntersectionObserver(containerRef, { rootMargin: "200px", freezeOnceVisible: true });

  useSlickSlideInert(containerRef, `${safeVideos.length}-${currentSlide}-${isInView}`);

  // Stable dependency for the products fetch so a new `videos` array reference
  // does not retrigger the request when the ids have not changed.
  const productIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    safeVideos.forEach((video) => {
      (video?.taggedProducts ?? []).forEach((p) => {
        if (p?.productId && !seen.has(p.productId)) {
          seen.add(p.productId);
          ids.push(p.productId);
        }
      });
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);
  const productIdsKey = productIds.join(",");

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    /**
     * Handles media query change by updating prefers reduced motion state.
     * @param {Event} event - The media query change event
     */
    const onChange = (event) => setPrefersReducedMotion(event.matches);
    mediaQuery.addEventListener?.("change", onChange);
    return () => mediaQuery.removeEventListener?.("change", onChange);
  }, []);

  /**
   * Fetches all product data for every video in a single API call and maps
   * each product back to its respective video by productId.
   */
  useEffect(() => {
    if (!isInView) {
      return undefined;
    }
    if (!productIdsKey) {
      setVideoProducts(safeVideos.map(() => []));
      setIsLoadingProducts(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingProducts(true);
    getProductsByIdsData(productIdsKey)
      .then((data) => {
        if (cancelled) {
          return;
        }
        const allProducts = getProductsFromByIdsResponse(data);
        const productMap = {};
        allProducts.forEach((p) => {
          if (p?.productId) {
            productMap[p.productId] = p;
          }
        });
        setVideoProducts(
          safeVideos.map((video) => (video?.taggedProducts ?? []).map((p) => productMap[p?.productId]).filter(Boolean)),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setVideoProducts(safeVideos.map(() => []));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingProducts(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsKey, isInView]);

  /**
   * Sets the currently playing video id.
   * @param {string} videoId - The id of the video to play
   */
  const onVideoPlay = (videoId) => {
    setPlayingVideoId(videoId);
  };

  /**
   * Clears the playing video id if this video was playing.
   * @param {string} videoId - The id of the video that paused
   */
  const onVideoPause = (videoId) => {
    if (playingVideoId === videoId) {
      setPlayingVideoId(null);
    }
  };

  /**
   * Handles slide change by updating the normalized slide index.
   * @param {number} _current - Previous slide index
   * @param {number} next - Next slide index
   */
  const onBeforeChange = (_current, next) => {
    setCurrentSlide(normalizeSlideIndex(next, safeVideos.length));
  };

  /**
   * Strips video sources from slick-cloned slides after init/reInit.
   * @returns {void}
   */
  const onSliderReady = useCallback(() => {
    stripClonedSlideVideos(containerRef.current);
  }, []);

  useEffect(() => {
    if (!isInView) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      stripClonedSlideVideos(containerRef.current);
    });
    return () => cancelAnimationFrame(frameId);
  }, [isInView, safeVideos.length]);

  // Auto-play only after the widget enters the viewport
  useEffect(() => {
    if (!isInView || prefersReducedMotion) {
      return;
    }
    const activeVideo = safeVideos[normalizeSlideIndex(currentSlide, safeVideos.length)];
    if (isMobile) {
      setPlayingVideoId(activeVideo?.id ?? safeVideos[0]?.id ?? null);
      return;
    }
    if (safeVideos[0]?.id) {
      setPlayingVideoId(safeVideos[0].id);
    }
  }, [isInView, isMobile, safeVideos, currentSlide, prefersReducedMotion]);

  if (safeVideos.length === 0) {
    return null;
  }

  const settings = {
    // speed: 5000,
    slidesToShow: isMobile ? 1 : 5.2,
    slidesToScroll: 1,
    arrows: !isMobile,
    dots: false,
    prevArrow: <CarouselArrow direction="prev" />,
    nextArrow: <CarouselArrow direction="next" />,
    centerMode: isMobile,
    centerPadding: isMobile ? "40px" : "0px",
    autoplay: false,
    beforeChange: onBeforeChange,
    onInit: onSliderReady,
    onReInit: onSliderReady,
    infinite: isMobile,
    hoverPause: true,
  };

  return (
    <div
      ref={containerRef}
      className="w-full"
      id={`video-trail-${safeVideos[0]?.id}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Video stories"
      style={{
        paddingLeft: isMobile ? undefined : sectionPadding?.paddingLeft,
        paddingRight: isMobile ? undefined : sectionPadding?.paddingRight,
      }}
    >
      {!isInView ? (
        <VideoTrailPlaceholder video={safeVideos[0]} />
      ) : (
        <Slider ref={sliderRef} {...settings}>
          {safeVideos.map((video, index) => {
            const isActive = index === normalizeSlideIndex(currentSlide, safeVideos.length);
            /**
             * Plays this video.
             */
            const onPlay = () => onVideoPlay(video.id);
            /**
             * Pauses this video.
             */
            const onPause = () => onVideoPause(video.id);
            return (
              <div key={video.id} className={isMobile ? "px-2" : "px-10"}>
                <div
                  className={prefersReducedMotion ? "" : "transition-transform duration-300"}
                  style={{
                    transform: isMobile && !prefersReducedMotion && !isActive ? "scale(0.9)" : "scale(1)",
                  }}
                >
                  <VideoCard
                    video={video}
                    isPlaying={playingVideoId === video.id}
                    isWidgetInView={isInView}
                    onPlay={onPlay}
                    onPause={onPause}
                    allVideos={safeVideos}
                    videoIndex={index}
                    products={videoProducts[index] ?? []}
                    isLoadingProducts={isLoadingProducts}
                    videoProducts={videoProducts}
                  />
                </div>
              </div>
            );
          })}
        </Slider>
      )}
    </div>
  );
};

VideoTrail.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMobile: PropTypes.bool.isRequired,
  sectionPadding: PropTypes.shape({
    paddingLeft: PropTypes.string,
    paddingRight: PropTypes.string,
  }).isRequired,
};

export default VideoTrail;
